import React, { useCallback } from "react";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import {
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorScreenProps,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { HeaderButton } from "../../../components";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { onlyUnique } from "../../../utils";
import { WAFileMainComponent } from "../../Storecast/Storecast/Widgets/WidgetFile/WAFile";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { OrgSpaceWidgetAddFileMutation } from "./__generated__/OrgSpaceWidgetAddFileMutation.graphql";
import { WAWidgetSizeToOverviewSize } from "../../Storecast/Storecast/WidgetAdd/utils/WAWidgetSizeToOverviewSize";
import { WidgetSizeT } from "../../Storecast/Storecast/localTypes/WidgetSizeT";
import { useOrgSpaceUnitSize } from "../OrgSpaces/useOrgSpaceUnitSize";
import { WorkspaceFilesScreenComponent } from "../WorkspaceFiles/WorkspaceFiles";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { AHIcon, useTheme } from "arkh";
import { WidgetFile } from "widgets/src/WidgetFile/WidgetFile";
import {
  FileInput,
  OrgSpaceWidgetAddFilePreviewQuery,
} from "./__generated__/OrgSpaceWidgetAddFilePreviewQuery.graphql";

const Stack = createNativeStackNavigator<ParamList>();
type ParamList = {
  OrgSpaceWAFileMain: {
    fileIds: string[];
    slideshowDuration: number;
  } & UserLoggedInNavigatorParamList["OrgSpaceWidgetAddFile"];
  OrgSpaceWAFileSelect: {
    fileIds: string[];
    slideshowDuration: number;
  } & UserLoggedInNavigatorParamList["OrgSpaceWidgetAddFile"];
  OrgSpaceWAAllFileSelect: {
    fileIds: string[];
    slideshowDuration: number;
  } & UserLoggedInNavigatorParamList["OrgSpaceWidgetAddFile"];
};

type OrgSpaceWAFileRouteProp<T extends keyof ParamList> = RouteProp<
  ParamList,
  T
>;

type OrgSpaceWAFileNavigationProp<T extends keyof ParamList> =
  CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, T>,
    NativeStackNavigationProp<UserLoggedInNavigatorParamList>
  >;

function OrgSpaceWAFileSelect(props: {
  navigation: OrgSpaceWAFileNavigationProp<"OrgSpaceWAFileSelect">;
  route: OrgSpaceWAFileRouteProp<"OrgSpaceWAFileSelect">;
}) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerOrgSpaceWAFileSelect {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerOrgSpaceWAFileSelect({
  navigation,
  route: { params },
  fetchKey,
}: {
  navigation: OrgSpaceWAFileNavigationProp<"OrgSpaceWAFileSelect">;
  route: OrgSpaceWAFileRouteProp<"OrgSpaceWAFileSelect">;
  fetchKey: string;
}) {
  const onPress = useCallback(
    (file: { id: string }) => {
      navigation.navigateDeprecated("OrgSpaceWAFileMain", {
        ...params,
        fileIds: [...params.fileIds, file.id].filter(onlyUnique),
      });
    },
    [navigation, params]
  );
  return (
    <WorkspaceFilesScreenComponent
      fetchKey={fetchKey}
      onFileItemPress={onPress}
      workspaceId={params.workspaceId}
      onUploadedFiles={(fileIds: string[]) => {
        navigation.navigateDeprecated("OrgSpaceWAFileMain", {
          ...params,
          fileIds: [...params.fileIds, ...fileIds].filter(onlyUnique),
        });
      }}
      itemActionComponent={<AHIcon type="add" color="primary" />}
      write
    />
  );
}

function OrgSpaceWAFileMain(props: {
  navigation: OrgSpaceWAFileNavigationProp<"OrgSpaceWAFileMain">;
  route: OrgSpaceWAFileRouteProp<"OrgSpaceWAFileMain">;
}) {
  const [_onCreate, loading] = useMutation<OrgSpaceWidgetAddFileMutation>(
    graphql`
      mutation OrgSpaceWidgetAddFileMutation(
        $spaceId: ID!
        $input: CreateWidgetFileInput!
      ) {
        storecastSpace(id: $spaceId) {
          createWidgetFile(widget: $input) {
            id
          }
        }
      }
    `
  );

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      if (props.route.params.fileIds) {
        _onCreate({
          variables: {
            spaceId: props.route.params.spaceId,
            input: {
              positionX:
                props.route.params.position.x +
                props.route.params.position.c * 2,
              positionY: props.route.params.position.y,
              size: WAWidgetSizeToOverviewSize(size),
              slideshowDuration: props.route.params.slideshowDuration,
              files: props.route.params.fileIds.map((fileId) => ({
                fileId,
              })),
            },
          },
          onCompleted: () => {
            props.navigation.pop(2);
          },
        });
      }
    },
    [
      _onCreate,
      props.navigation,
      props.route.params.fileIds,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.slideshowDuration,
      props.route.params.spaceId,
    ]
  );

  const unitSize = useOrgSpaceUnitSize();

  return (
    <WAFileMainComponent
      fileIds={props.route.params.fileIds}
      loading={loading}
      onCreate={onCreate}
      maxSize={props.route.params.maxSize}
      onAddFile={() => {
        props.navigation.navigateDeprecated("OrgSpaceWAFileSelect", {
          ...props.route.params,
        });
      }}
      slideshowDuration={props.route.params.slideshowDuration}
      unitSize={unitSize}
      setSlideShowDuration={(duration) => {
        props.navigation.setParams({
          slideshowDuration: duration,
        });
      }}
      onRemoveFile={(removeId) => {
        props.navigation.setParams({
          fileIds: props.route.params.fileIds.filter((f) => f !== removeId),
        });
      }}
      onSetOrder={(order) => {
        props.navigation.setParams({
          fileIds: order,
        });
      }}
      preview={
        <OrgSpaceWidgetAddFilePreview
          files={props.route.params.fileIds.map((fileId) => ({ fileId }))}
        />
      }
    />
  );
}

function OrgSpaceWidgetAddFilePreview(props: { files: FileInput[] }) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerOrgSpaceWidgetAddFilePreview {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerOrgSpaceWidgetAddFilePreview(props: {
  files: FileInput[];
  fetchKey: string;
}) {
  const data = useLazyLoadQuery<OrgSpaceWidgetAddFilePreviewQuery>(
    graphql`
      query OrgSpaceWidgetAddFilePreviewQuery($input: PreviewWidgetFileInput!) {
        previewWidgetFile(input: $input) {
          ...WidgetFile_widgetFile
        }
      }
    `,
    {
      input: {
        files: props.files,
      },
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  return (
    <WidgetFile mode="IDLE" idiom="STATIC" widget={data.previewWidgetFile} />
  );
}

function InnerOrgSpaceWidgetAddFile(
  props: UserLoggedInNavigatorScreenProps<"OrgSpaceWidgetAddFile">
) {
  const intl_tokens = useIntlTokens();
  const theme = useTheme();
  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: true,
        headerStyle: {
          backgroundColor: theme.background,
        },
      }}
    >
      <Stack.Screen
        name="OrgSpaceWAFileMain"
        component={OrgSpaceWAFileMain}
        options={{
          title: intl_tokens.Files,
          headerLeft: () => (
            <HeaderButton type="BACK" onPress={props.navigation.goBack} />
          ),
        }}
        initialParams={{ ...props.route.params, fileIds: [] }}
      />
      <Stack.Screen
        name="OrgSpaceWAFileSelect"
        component={OrgSpaceWAFileSelect}
        options={{
          title: "",
          headerShadowVisible: false,
          contentStyle: {
            backgroundColor: theme["surface-background-light"],
          },
          headerStyle: {
            backgroundColor: theme["surface-background-light"],
          },
        }}
        initialParams={{ ...props.route.params, fileIds: [] }}
      />
    </Stack.Navigator>
  );
}

export const OrgSpaceWidgetAddFile = withWebModal(InnerOrgSpaceWidgetAddFile);
